import to from 'await-to-js';
import React, { useEffect, useMemo, useState } from 'react';
import {
    alpha,
    Box,
    ButtonBase,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { PermissionName, type UsageFeatures, type UsageOrganisationType } from 'c-sdk';
import { usageIcon } from 'c-main/icons';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { LoadingSpinner } from 'c-components';
import { formatNumberWhole } from 'c-lib';
import { NetworkRequestState } from 'c-data-layer';
import {
    PageStack,
    tableHeadSx,
    UsageSection,
    PeriodSelect,
    ShareBar,
    StaffToggle,
    SummaryCell,
    SummaryStrip,
    UsageEmpty,
    UsageHeader,
    UsageLoadError,
} from './UsageParts';
import { DEFAULT_PERIOD_DAYS, percentOf, periodFor, useFormatDuration } from './usageLib';
import { areaFor } from './usageAreas';
import { pageLabel, useActivityLabel } from './activityLabels';
import { ROLES, roleShare, summariseFeatures, type FeatureSummary } from './featureUsage';

const NUMBER_COLUMN = 104;

/** Pages shown in "Most used" before "Show all"; area groups list this many names each. */
const TOP_PAGES = 10;
const NAMES_PER_AREA = 3;

const MoreToggle = ({
    open,
    label,
    onClick,
}: {
    open: boolean;
    label: string;
    onClick: () => void;
}) => {
    const t = useCommonTranslation();
    return (
        <ButtonBase
            onClick={onClick}
            aria-expanded={open}
            sx={{
                alignSelf: 'flex-start',
                minHeight: 44,
                color: 'primary.main',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
            }}
        >
            {open ? t('Modules.Admin.Usage.showFewer') : label}
        </ButtonBase>
    );
};

const RoleSelect = ({
    value,
    onChange,
}: {
    value: UsageOrganisationType | '';
    onChange: (role: UsageOrganisationType | '') => void;
}) => {
    const t = useCommonTranslation();
    return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="usage-role-label" shrink>
                {t('Modules.Admin.Usage.columns.role')}
            </InputLabel>
            <Select
                labelId="usage-role-label"
                label={t('Modules.Admin.Usage.columns.role')}
                value={value}
                displayEmpty
                notched
                onChange={event => onChange(event.target.value as UsageOrganisationType | '')}
            >
                <MenuItem value="">{t('Modules.Admin.Usage.everyone')}</MenuItem>
                {ROLES.map(role => (
                    <MenuItem key={role} value={role}>
                        {t(`Modules.Admin.Usage.types.${role}`)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/** The page's area, unless the page is named the same ("Campaign reporting" in Campaign reporting). */
const AreaTag = ({ template }: { template: string }) => {
    const t = useCommonTranslation();
    const area = t(`Modules.Admin.Usage.areas.${areaFor(template)}`);
    if (area === pageLabel(template)) return null;
    return (
        <Box
            component="span"
            sx={{
                fontSize: 11,
                fontWeight: 500,
                color: 'primary.dark',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                borderRadius: 999,
                px: 1,
                py: '2px',
                whiteSpace: 'nowrap',
            }}
        >
            {area}
        </Box>
    );
};

const MostUsedPages = ({ summary }: { summary: FeatureSummary }) => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const [all, setAll] = useState(false);
    const maxShare = Math.max(0.0001, ...summary.ranked.map(page => page.time_share));
    const shown = all ? summary.ranked : summary.ranked.slice(0, TOP_PAGES);
    const right = {
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        width: NUMBER_COLUMN,
    } as const;

    return (
        <UsageSection
            title={t('Modules.Admin.Usage.mostUsedPages')}
            hint={t('Modules.Admin.Usage.mostUsedPagesHint')}
        >
            {shown.length === 0 ? (
                <UsageEmpty />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={tableHeadSx}>
                            <TableRow>
                                <TableCell>{t('Modules.Admin.Usage.columns.page')}</TableCell>
                                <TableCell sx={right}>
                                    {t('Modules.Admin.Usage.columns.people')}
                                </TableCell>
                                <TableCell sx={right}>
                                    {t('Modules.Admin.Usage.columns.organisations')}
                                </TableCell>
                                <TableCell sx={right}>
                                    {t('Modules.Admin.Usage.columns.visits')}
                                </TableCell>
                                <TableCell sx={right}>
                                    {t('Modules.Admin.Usage.columns.avgTime')}
                                </TableCell>
                                <TableCell sx={{ width: 240, pl: 3 }}>
                                    {t('Modules.Admin.Usage.columns.shareOfTime')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map(page => (
                                <TableRow key={page.name}>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography variant="body2" fontWeight={500}>
                                                {pageLabel(page.name)}
                                            </Typography>
                                            <AreaTag template={page.name} />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={right}>{page.people}</TableCell>
                                    <TableCell sx={right}>{page.organisations}</TableCell>
                                    <TableCell sx={right}>
                                        {formatNumberWhole(page.visits)}
                                    </TableCell>
                                    <TableCell sx={{ ...right, color: 'text.secondary' }}>
                                        {formatDuration(page.avg_dwell_ms)}
                                    </TableCell>
                                    <TableCell sx={{ pl: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <ShareBar
                                                    percent={percentOf(page.time_share, maxShare)}
                                                    label={pageLabel(page.name)}
                                                />
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ ...right, width: 40 }}
                                            >
                                                {Math.round(page.time_share * 100)}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {summary.ranked.length > TOP_PAGES && (
                <MoreToggle
                    open={all}
                    onClick={() => setAll(open => !open)}
                    label={t('Modules.Admin.Usage.showAllPages', { count: summary.ranked.length })}
                />
            )}
        </UsageSection>
    );
};

const NeverUsed = ({ summary }: { summary: FeatureSummary }) => {
    const t = useCommonTranslation();
    const [all, setAll] = useState(false);
    const hasMore = summary.neverUsed.some(group => group.templates.length > NAMES_PER_AREA);

    return (
        <UsageSection
            title={t('Modules.Admin.Usage.neverUsedList')}
            hint={t('Modules.Admin.Usage.neverUsedListHint')}
        >
            {summary.neverUsed.map(group => {
                const names = group.templates.map(pageLabel);
                const shown = all ? names : names.slice(0, NAMES_PER_AREA);
                const more = names.length - shown.length;
                return (
                    <Box
                        key={group.area}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            pt: 1.5,
                            borderTop: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography
                                component="h3"
                                variant="overline"
                                sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}
                            >
                                {t(`Modules.Admin.Usage.areas.${group.area}`)}
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color="warning.dark">
                                {t('Modules.Admin.Usage.pageCount', {
                                    count: group.templates.length,
                                })}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            {shown.join(' · ')}
                            {more > 0 && (
                                <Typography component="span" variant="body2" color="text.secondary">
                                    {' · '}
                                    {t('Modules.Admin.Usage.moreNames', { count: more })}
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                );
            })}
            {summary.neverUsed.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {t('Modules.Admin.Usage.nothingUnused')}
                </Typography>
            )}
            {hasMore && (
                <MoreToggle
                    open={all}
                    onClick={() => setAll(open => !open)}
                    label={t('Modules.Admin.Usage.seeAllNever', {
                        count: summary.neverUsedCount,
                    })}
                />
            )}
        </UsageSection>
    );
};

/** Background and text for an adoption share: darker is more of the role. */
const useAdoptionColours = () => {
    const theme = useTheme();
    const { primary, warning, text } = theme.palette;
    return (share: number | null) => {
        if (share == null) return { bg: 'transparent', fg: text.secondary };
        if (share >= 0.7) return { bg: primary.dark, fg: primary.contrastText };
        if (share >= 0.4) return { bg: primary.main, fg: primary.contrastText };
        if (share >= 0.15) return { bg: alpha(primary.main, 0.18), fg: text.primary };
        return { bg: alpha(warning.main, 0.16), fg: text.primary };
    };
};

const WhoUsesWhat = ({
    summary,
    activeByRole,
    roles,
}: {
    summary: FeatureSummary;
    activeByRole: UsageFeatures['active_users_by_role'];
    roles: UsageOrganisationType[];
}) => {
    const t = useCommonTranslation();
    const activityLabel = useActivityLabel();
    const colours = useAdoptionColours();
    const key = (
        <Box
            aria-hidden="true"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
        >
            <Typography variant="caption" color="text.secondary">
                {t('Modules.Admin.Usage.few')}
            </Typography>
            {[0.05, 0.2, 0.5, 0.8].map(level => (
                <Box
                    key={level}
                    sx={{ width: 22, height: 12, borderRadius: 0.5, bgcolor: colours(level).bg }}
                />
            ))}
            <Typography variant="caption" color="text.secondary">
                {t('Modules.Admin.Usage.most')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {t('Modules.Admin.Usage.noneActiveKey')}
            </Typography>
        </Box>
    );

    return (
        <UsageSection
            title={t('Modules.Admin.Usage.adoptionByRole')}
            hint={t('Modules.Admin.Usage.adoptionHint')}
            aside={key}
        >
            {summary.adoption.length === 0 ? (
                <UsageEmpty />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={tableHeadSx}>
                            <TableRow>
                                <TableCell>{t('Modules.Admin.Usage.columns.feature')}</TableCell>
                                <TableCell sx={{ width: 96 }}>
                                    {t('Modules.Admin.Usage.columns.kind')}
                                </TableCell>
                                {roles.map(role => (
                                    <TableCell key={role} sx={{ textAlign: 'center', width: 140 }}>
                                        {t(`Modules.Admin.Usage.types.${role}`)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {summary.adoption.map(row => (
                                <TableRow key={row.key}>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {row.kind === 'page'
                                            ? pageLabel(row.name)
                                            : activityLabel({ kind: 'event', name: row.name })}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary' }}>
                                        {t(`Modules.Admin.Usage.featureKinds.${row.kind}`)}
                                    </TableCell>
                                    {roles.map(role => {
                                        const share = roleShare(row.roles, role, activeByRole);
                                        const { bg, fg } = colours(share);
                                        return (
                                            <TableCell key={role} sx={{ textAlign: 'center' }}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-block',
                                                        minWidth: 64,
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        fontVariantNumeric: 'tabular-nums',
                                                        bgcolor: bg,
                                                        color: fg,
                                                    }}
                                                >
                                                    {share == null
                                                        ? t('Modules.Admin.Usage.notApplicable')
                                                        : `${Math.round(share * 100)}%`}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </UsageSection>
    );
};

const UsageFeaturesPage = () => {
    const t = useCommonTranslation();
    const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
    const [includeStaff, setIncludeStaff] = useState(false);
    const [role, setRole] = useState<UsageOrganisationType | ''>('');
    const query = useMemo(
        () => ({
            ...periodFor(days),
            include_staff: includeStaff ? (1 as const) : (0 as const),
            ...(role ? { role } : {}),
        }),
        [days, includeStaff, role],
    );
    const { start, data, requestState } = useAPIClientRequest(ApiClient.Usage.features);

    useEffect(() => {
        to(start(query));
    }, [query, start]);

    const features = data?.data?.data;
    const summary = useMemo(
        () => (features ? summariseFeatures(features, includeStaff) : null),
        [features, includeStaff],
    );

    return (
        <PageStack>
            <UsageHeader
                title={t('Modules.Admin.Usage.featuresTitle')}
                subtitle={t('Modules.Admin.Usage.featuresDescription')}
                controls={
                    <>
                        <RoleSelect value={role} onChange={setRole} />
                        <StaffToggle checked={includeStaff} onChange={setIncludeStaff} />
                        <PeriodSelect value={days} onChange={setDays} />
                    </>
                }
            />

            {requestState === NetworkRequestState.Error && <UsageLoadError />}
            {requestState === NetworkRequestState.InProgress && !features && <LoadingSpinner />}

            {features && summary && (
                <>
                    <SummaryStrip>
                        <SummaryCell
                            label={t('Modules.Admin.Usage.pagesInApp')}
                            value={summary.pageCount}
                            note={t(
                                includeStaff
                                    ? 'Modules.Admin.Usage.pagesInAppHint'
                                    : 'Modules.Admin.Usage.pagesInAppHintNoAdmin',
                            )}
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.widelyUsed')}
                            value={summary.regular}
                            note={t('Modules.Admin.Usage.widelyUsedHint', {
                                share: Math.round(summary.regularShare * 100),
                            })}
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.rarelyUsed')}
                            value={summary.rare}
                            note={t('Modules.Admin.Usage.rarelyUsedHint')}
                        />
                        <Box sx={{ bgcolor: theme => alpha(theme.palette.warning.main, 0.1) }}>
                            <SummaryCell
                                label={t('Modules.Admin.Usage.neverUsed')}
                                value={summary.neverUsedCount}
                                note={t('Modules.Admin.Usage.neverUsedHint')}
                            />
                        </Box>
                    </SummaryStrip>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box sx={{ flex: '999 1 620px', minWidth: 0 }}>
                            <MostUsedPages summary={summary} />
                        </Box>
                        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                            <NeverUsed summary={summary} />
                        </Box>
                    </Box>

                    <WhoUsesWhat
                        summary={summary}
                        activeByRole={features.active_users_by_role}
                        roles={role ? [role] : ROLES}
                    />
                </>
            )}
        </PageStack>
    );
};

const setup: PageConfig = {
    id: 'UsageFeaturesPage',
    component: UsageFeaturesPage,
    systemSearch: {
        title: 'Modules.Admin.Usage.featuresTitle',
        description: 'Modules.Admin.Usage.featuresDescription',
        type: 'page',
        saveAsRecentPage: true,
        aliases: undefined,
        icon: usageIcon,
        route: PostAuthRoutes.Admin.UsageFeatures,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.Admin.Usage.metaTitle', link: PostAuthRoutes.Admin.UsageOverview },
            { label: 'Modules.Admin.Usage.featuresTitle' },
        ],
        permissions: [PermissionName.AdminCan_view_usage],
    },
};

export default setup;
